import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { db } from "@/firebase/firebase-config";

// Slugify function to match server-side slugification
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120) || "lesson";
}

export type UserProfile = {
  uid: string;
  displayName?: string | null;
  age?: number;
  education?: string | null;
  interests?: string[];
  values?: string[];
  [key: string]: unknown;
};

export type Course = {
  id: string;
  title: string;
  description?: string;
  level?: string;
  learningObjectives?: string[];
  mainConceptSummary?: string | string[];
  faces?: Array<"resistance" | "rebound" | "growth">;
  method?: "CBT" | "ACT" | "mixed";
  lessonTags?: string[];
  isCoreLesson?: boolean;
  prerequisiteLessonIds?: string[];
  badgeUrl?: string;
  imageUrl?: string;
  domainId?: string;
  [key: string]: unknown;
};

export type LessonActivities = {
  conceptMatch?: {
    version: number;
    instructions: string;
    pairs: Array<{
      id: string;
      concept: string;
      definition: string;
    }>;
  };
  reflection?: {
    version: number;
    prompt: string;
    minWords: number;
    rubric: {
      relevanceMax: number;
      understandingMax: number;
    };
  };
};

export type ReflectionScoreResult = {
  relevanceScore: number;
  understandingScore: number;
  totalScore: number;
  message: string;
  reinforcementSummary: string;
  improvementFeedback?: string;
};

export type LessonProgress = {
  audioComplete?: boolean;
  audioCompletedAt?: unknown;
  dragDropComplete?: boolean;
  dragDropCompletedAt?: unknown;
  reflectionComplete?: boolean;
  reflection?: {
    prompt: string;
    text: string;
    wordCount: number;
    scoredAt?: unknown;
    score?: ReflectionScoreResult;
  };
  points?: {
    dragDropAwarded?: boolean;
    reflectionAwarded?: boolean;
  };
};

export type QuizResponse = {
  index: number;
  question: string;
  answer: string | null;
  correctAnswer?: string;
  isCorrect: boolean;
};

export type LessonDoc = {
  id?: string;
  title: string;
  lessonScript?: string;
  faq?: { question: string; answer: string }[];
  assessmentQuestions?: {
    question: string;
    choices: string[];
    correctAnswer?: string;
  }[];
  activities?: LessonActivities | null;
  audioUrl?: string;
  audioSignedUrl?: string;
  audioCompleted?: boolean;
  quizTaken?: boolean;
  quiz?: {
    taken?: boolean;
    correctCount?: number;
    questionPoints?: number;
    completionPoints?: number;
    totalAwarded?: number;
    responses?: QuizResponse[];
  };
  completed?: boolean;
  progress?: LessonProgress;
  // New curriculum fields
  faces?: Array<"resistance" | "rebound" | "growth">;
  method?: "CBT" | "ACT" | "mixed";
  lessonTags?: string[];
  isCoreLesson?: boolean;
  prerequisiteLessonIds?: string[];
  level?: string;
  badgeUrl?: string;
  domainId?: string;
  [key: string]: unknown;
};

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const profileRef = doc(db(), "users", uid);
  const snapshot = await getDoc(profileRef);

  if (!snapshot.exists()) return null;

  const data = snapshot.data() as UserProfile;
  return { ...data, uid };
}

export async function getCourseFromCurriculum(domainId: string, lessonId: string): Promise<Course | null> {
  const lessonRef = doc(db(), "curriculum", domainId, "lessons", lessonId);
  const snapshot = await getDoc(lessonRef);

  if (!snapshot.exists()) return null;

  const data = snapshot.data() as Course;
  return { ...data, id: lessonId };
}

export async function getUserLesson(uid: string, courseId: string): Promise<LessonDoc | null> {
  const slugifiedId = slugify(courseId);
  const lessonRef = doc(db(), "users", uid, "geminiLessons", slugifiedId);
  const snapshot = await getDoc(lessonRef);

  if (!snapshot.exists()) return null;

  const data = snapshot.data() as LessonDoc;
  return { ...data, id: courseId };
}

export async function saveUserLesson(uid: string, courseId: string, lesson: LessonDoc) {
  const slugifiedId = slugify(courseId);
  const lessonRef = doc(db(), "users", uid, "geminiLessons", slugifiedId);
  await setDoc(
    lessonRef,
    { createdAt: serverTimestamp(), ...lesson },
    { merge: false }
  );
}

export async function upsertQuizResults(
  uid: string,
  courseId: string,
  payload: {
    correctCount: number;
    questionPoints: number;
    completionPoints: number;
    totalAwarded: number;
    responses: QuizResponse[];
  }
) {
  const lessonRef = doc(db(), "users", uid, "geminiLessons", courseId);

  await setDoc(
    lessonRef,
    {
      quizTaken: true,
      completed: true,
      quiz: {
        taken: true,
        correctCount: payload.correctCount,
        questionPoints: payload.questionPoints,
        completionPoints: payload.completionPoints,
        totalAwarded: payload.totalAwarded,
        responses: payload.responses,
      },
      quizSubmittedAt: serverTimestamp(),
      completedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function updateUserCompletedCourses(uid: string, courseId: string) {
  const userRef = doc(db(), "users", uid);
  await updateDoc(userRef, {
    completedCourses: arrayUnion(courseId),
  });
}
